import { useEffect, useMemo, useState } from 'react';
import {
  FolderOpen,
  ImageSquare,
  FilmStrip,
  MusicNotes,
  FileText,
  MagnifyingGlass,
  BookOpen
} from 'phosphor-react';
import { AnimatePresence, motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Page } from '../components/layout/Page';
import { AssetPreviewModal } from '../components/Library/AssetPreviewModal';
import { AssetShelf } from '../components/Library/AssetShelf';
import assetsData from '../data/publicAssets.json';
import { CATEGORY_META, LESSON_LIBRARY, TYPE_LABELS } from '../lib/libraryConfig';
import {
  describeAsset,
  fixMojibake,
  formatBytes,
  formatLabel,
  getTypeByExt,
  buildPublicUrl,
  getUsageHint,
  buildDisplayName
} from '../lib/libraryUtils';

const TYPE_ICONS = {
  image: ImageSquare,
  video: FilmStrip,
  audio: MusicNotes,
  document: FileText,
  other: FolderOpen
};

const previewGradientByType = {
  video: 'linear-gradient(135deg, #0f172a 0%, #0ea5e9 120%)',
  audio: 'linear-gradient(135deg, #0f172a 0%, #10b981 120%)',
  document: 'linear-gradient(135deg, #0f172a 0%, #f59e0b 120%)',
  other: 'linear-gradient(135deg, #0f172a 0%, #a78bfa 120%)'
};

const VISIBLE_ASSET_TYPES = new Set(['image', 'video']);

const listVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.97, filter: 'blur(6px)' },
  visible: { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }
};

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const decodeArabicMojibake = (value = '') => {
  if (typeof value !== 'string' || !/[Ã˜Ã™Ãƒ]/.test(value)) return value;

  try {
    const bytes = Uint8Array.from(Array.from(value).map((char) => char.charCodeAt(0)));
    const decoded = new TextDecoder('utf-8').decode(bytes);
    return /[\u0600-\u06ff]/.test(decoded) ? decoded : value;
  } catch {
    return value;
  }
};

export const PublicLibrary = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { scrollYProgress } = useScroll();
  const scrollProgress = useSpring(scrollYProgress, { stiffness: 140, damping: 28, mass: 0.2 });
  const orbRightY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const orbLeftY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  const assets = useMemo(() => {
    return assetsData
      .map((item, index) => {
        const fixedPath = fixMojibake(item.path);
        const parts = fixedPath.split('/').filter(Boolean);
        const filename = parts[parts.length - 1] || '';
        const category = parts.length > 1 ? parts[0] : 'root';
        const group = parts.length > 2 ? parts.slice(1, -1).join('/') : '';
        const type = getTypeByExt(item.ext);
        const rawName = formatLabel(filename) || filename;
        const categoryLabel = decodeArabicMojibake(CATEGORY_META[category]?.title || category);
        const sourceLabel = group ? formatLabel(group.split('/').pop()) : categoryLabel;
        const displayName = buildDisplayName({ name: rawName, filename, type, sourceLabel, index });

        return {
          id: `asset-${index}`,
          path: fixedPath,
          size: item.size,
          ext: item.ext,
          type,
          name: rawName,
          displayName,
          filename,
          category,
          categoryLabel,
          group,
          sourceLabel,
          description: decodeArabicMojibake(describeAsset({ name: rawName, filename, ext: item.ext, type })),
          usageHint: decodeArabicMojibake(getUsageHint({ type, ext: item.ext }))
        };
      })
      .filter((asset) => VISIBLE_ASSET_TYPES.has(asset.type));
  }, []);

  const [query, setQuery] = useState('');
  const [activeType, setActiveType] = useState('all');
  const [activeLesson, setActiveLesson] = useState('all');
  const [previewAsset, setPreviewAsset] = useState(null);

  useEffect(() => {
    const lesson = searchParams.get('lesson');
    const type = searchParams.get('type');
    setActiveLesson(lesson || 'all');
    const nextType = (type || 'all').toLowerCase();
    setActiveType(nextType === 'all' || VISIBLE_ASSET_TYPES.has(nextType) ? nextType : 'all');
  }, [searchParams]);

  const activeLessonMeta =
    activeLesson !== 'all' ? LESSON_LIBRARY.find((lesson) => lesson.id === activeLesson) : null;

  const filteredAssets = useMemo(() => {
    const q = query.trim().toLowerCase();

    return assets
      .filter((asset) => !asset.filename.startsWith('~$'))
      .filter((asset) => {
        if (!activeLessonMeta) return true;
        if (!activeLessonMeta.categories.includes(asset.category)) return false;
        if (!activeLessonMeta.includeKeywords || activeLessonMeta.includeKeywords.length === 0) return true;
        const hay = `${asset.name} ${asset.displayName} ${asset.filename}`.toLowerCase();
        return activeLessonMeta.includeKeywords.some((keyword) => hay.includes(keyword.toLowerCase()));
      })
      .filter((asset) => (activeType === 'all' ? true : asset.type === activeType))
      .filter((asset) => {
        if (!q) return true;
        const hay = `${asset.displayName} ${asset.description} ${asset.sourceLabel} ${asset.categoryLabel}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [assets, query, activeLessonMeta, activeType]);

  const stats = useMemo(() => {
    const totalSize = filteredAssets.reduce((acc, asset) => acc + (asset.size || 0), 0);
    const counts = filteredAssets.reduce(
      (acc, asset) => {
        acc[asset.type] = (acc[asset.type] || 0) + 1;
        return acc;
      },
      {}
    );
    return { totalSize, counts };
  }, [filteredAssets]);

  const groupedByType = useMemo(
    () =>
      filteredAssets.reduce(
        (acc, item) => {
          acc[item.type].push(item);
          return acc;
        },
        { image: [], video: [], audio: [], document: [], other: [] }
      ),
    [filteredAssets]
  );

  const typeOptions = useMemo(() => {
    const set = new Set(assets.map((asset) => asset.type));
    return ['all', ...Array.from(set)];
  }, [assets]);

  const updateUrl = (nextLesson, nextType) => {
    const params = {};
    if (nextLesson && nextLesson !== 'all') params.lesson = nextLesson;
    if (nextType && nextType !== 'all') params.type = nextType;
    setSearchParams(params);
  };

  const clearFilters = () => {
    setQuery('');
    setActiveType('all');
    setActiveLesson('all');
    setSearchParams({});
  };

  const openPreview = (asset) => {
    setPreviewAsset(asset);
  };

  const closePreview = () => {
    setPreviewAsset(null);
  };

  return (
    <Page className="space-y-6">
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 z-[1300] pointer-events-none"
        style={{
          scaleX: scrollProgress,
          transformOrigin: 'right center',
          background: 'linear-gradient(90deg, #22d3ee 0%, #60a5fa 45%, #f472b6 100%)'
        }}
      />

      <motion.section
        className="glass-panel p-7 md:p-10 relative overflow-hidden"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={sectionVariants}
        transition={{ duration: 0.45, ease: 'easeOut' }}
      >
        <motion.div className="absolute -top-28 -right-24 w-72 h-72 rounded-full bg-cyan-300/25 blur-3xl" style={{ y: orbRightY }} />
        <motion.div className="absolute -bottom-28 -left-24 w-80 h-80 rounded-full bg-pink-300/18 blur-3xl" style={{ y: orbLeftY }} />

        <div className="relative space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h1 className="text-4xl md:text-5xl font-black text-gray-900">المكتبة التعليمية</h1>
            <div className="flex flex-wrap gap-2">
              <span className="chip chip-strong">{filteredAssets.length} عنصر</span>
              <span className="chip">الحجم الكلي: {formatBytes(stats.totalSize)}</span>
            </div>
          </div>

          <p className="text-gray-700 max-w-3xl">
            اختر الدرس أو النوع، ثم اضغط على أي صورة أو فيديو لعرضه داخل نفس الصفحة.
          </p>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setActiveLesson('all');
                updateUrl('all', activeType);
              }}
              className={`chip ${activeLesson === 'all' ? 'chip-strong' : ''}`}
            >
              <BookOpen size={16} weight="bold" />
              كل المكتبة
            </button>
            {LESSON_LIBRARY.map((lesson) => (
              <button
                key={lesson.id}
                type="button"
                onClick={() => {
                  setActiveLesson(lesson.id);
                  setQuery('');
                  updateUrl(lesson.id, activeType);
                }}
                className={`chip ${activeLesson === lesson.id ? 'chip-strong' : ''}`}
              >
                <BookOpen size={16} weight="bold" />
                {decodeArabicMojibake(lesson.title)}
              </button>
            ))}
          </div>

          {activeLessonMeta && (
            <div className="rounded-3xl border border-white/40 bg-white/65 p-5">
              <p className="text-xl font-black text-gray-900">{decodeArabicMojibake(activeLessonMeta.title)}</p>
              <p className="text-sm text-gray-700 mt-1">{decodeArabicMojibake(activeLessonMeta.description)}</p>
              <div className="mt-3">
                <Link to={`/lessons/${activeLessonMeta.id}`} className="chip chip-strong">
                  افتح الدرس
                </Link>
              </div>
            </div>
          )}
        </div>
      </motion.section>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
        variants={sectionVariants}
        transition={{ duration: 0.35, ease: 'easeOut' }}
      >
        <Card className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px] relative">
            <MagnifyingGlass className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="ابحث باسم المصدر أو الوصف..."
              className="input-text pr-12"
            />
          </div>
          <Button variant="secondary" onClick={clearFilters} className="text-sm px-4 py-2">
            مسح الفلاتر
          </Button>
        </div>

        <div className="flex gap-2 flex-wrap">
          {typeOptions.map((type) => {
            const isActive = activeType === type;
            const Icon = type === 'all' ? FolderOpen : TYPE_ICONS[type];
            const label = type === 'all' ? 'كل الأنواع' : decodeArabicMojibake(TYPE_LABELS[type]);
            return (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setActiveType(type);
                  updateUrl(activeLesson, type);
                }}
                className={`chip ${isActive ? 'chip-strong' : ''}`}
              >
                <Icon size={16} weight="bold" />
                {label}
                {type !== 'all' && <span className="number">{stats.counts[type] || 0}</span>}
              </button>
            );
          })}
        </div>
        </Card>
      </motion.div>

      <motion.section
        className="space-y-3"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={sectionVariants}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-black text-gray-900">المصادر</h2>
          <span className="chip">{filteredAssets.length} عنصر</span>
        </div>

        {filteredAssets.length === 0 ? (
          <Card>
            <p className="text-gray-700">لا توجد نتائج مطابقة. جرّب كلمة بحث أخرى.</p>
          </Card>
        ) : activeLesson !== 'all' ? (
          <div className="space-y-6">
            <AssetShelf title="🖼️ صور مرتبطة بالدرس" items={groupedByType.image} onPreview={openPreview} typeIcons={TYPE_ICONS} typeLabels={TYPE_LABELS} />
            <AssetShelf title="🎬 فيديوهات مرتبطة بالدرس" items={groupedByType.video} onPreview={openPreview} typeIcons={TYPE_ICONS} typeLabels={TYPE_LABELS} />
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredAssets.map((asset) => {
                const Icon = TYPE_ICONS[asset.type];
                const publicUrl = buildPublicUrl(asset.path);

                return (
                  <motion.div
                    key={asset.id}
                    layout
                    initial="hidden"
                    whileInView="visible"
                    exit="hidden"
                    variants={listVariants}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.36, ease: 'easeOut' }}
                    whileHover={{ y: -8, scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    className="rounded-3xl border border-white/50 bg-white/70 p-3 space-y-3 will-change-transform"
                  >
                    <button
                      type="button"
                      onClick={() => openPreview(asset)}
                      className="rounded-2xl border border-white/40 bg-white/60 overflow-hidden w-full text-right cursor-pointer group"
                    >
                      {asset.type === 'video' ? (
                        <video
                          src={publicUrl}
                          autoPlay
                          muted
                          loop
                          playsInline
                          preload="metadata"
                          className="w-full h-44 object-cover pointer-events-none transition duration-500 group-hover:scale-105"
                        />
                      ) : asset.type === 'image' ? (
                        <img
                          src={publicUrl}
                          alt={asset.displayName}
                          className="w-full h-44 object-contain bg-gradient-to-br from-slate-50 to-cyan-50 p-2 pointer-events-none transition duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="w-full h-44 flex flex-col items-center justify-center text-white font-black gap-2"
                          style={{ background: previewGradientByType[asset.type] || previewGradientByType.other }}
                        >
                          <span className="text-lg">{decodeArabicMojibake(TYPE_LABELS[asset.type])}</span>
                        </div>
                      )}
                    </button>

                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-9 h-9 rounded-2xl bg-gray-900 text-white flex items-center justify-center flex-shrink-0">
                          <Icon size={18} weight="fill" />
                        </div>
                        <div>
                          <p className="font-black text-gray-900 leading-tight">{asset.displayName}</p>
                          <p className="text-xs text-gray-600 line-clamp-2">{asset.description}</p>
                          <p className="text-xs text-gray-600 mt-1">المصدر: {asset.sourceLabel}</p>
                        </div>
                      </div>
                      <button type="button" className="chip chip-strong" onClick={() => openPreview(asset)}>
                        معاينة
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        )}
      </motion.section>

      {previewAsset && <AssetPreviewModal key={previewAsset.id} asset={previewAsset} onClose={closePreview} />}
    </Page>
  );
};
